<script setup lang="ts">
import { computed, onScopeDispose, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppDialog from '../../primitives/AppDialog/AppDialog.vue'
import AppSpinner from '../../primitives/AppSpinner/AppSpinner.vue'
import AppEmptyState from '../../primitives/AppEmptyState/AppEmptyState.vue'
import DesignThumbnail from '../../design/DesignThumbnail/DesignThumbnail.vue'
import { useDesignStore } from '../../../stores/design'
import { useContentStore } from '../../../stores/content'
import { isLoaded, isLoading } from '../../../types/load-state'
import type { Design } from '../../../types/design'

const open = defineModel<boolean>('open', { default: false })

const props = defineProps<{
  /** The post the picked design attaches to as its artwork. */
  postId: string
}>()

const { t } = useI18n()
const design = useDesignStore()
const content = useContentStore()

const subscribedIds = new Set<string>()

function syncRunSubscriptions(designs: Design[]): void {
  const ids = new Set(designs.map((item) => item.id))
  for (const id of ids) {
    if (!subscribedIds.has(id)) {
      design.subscribeToDesign(id)
      subscribedIds.add(id)
    }
  }
  for (const id of Array.from(subscribedIds)) {
    if (!ids.has(id)) {
      design.unsubscribeFromDesign(id)
      subscribedIds.delete(id)
    }
  }
}

let releaseRecent: (() => void) | null = null

watch(
  open,
  (isOpen) => {
    if (isOpen) releaseRecent ??= design.retainRecentDesigns()
    else {
      releaseRecent?.()
      releaseRecent = null
    }
  },
  { immediate: true },
)

watch(
  () => design.recentDesigns,
  (state) => {
    if (isLoaded(state)) syncRunSubscriptions(state.value)
  },
  { immediate: true },
)

onScopeDispose(() => {
  releaseRecent?.()
  releaseRecent = null
  for (const id of subscribedIds) design.unsubscribeFromDesign(id)
  subscribedIds.clear()
})

const isLoadingDesigns = computed(() => isLoading(design.recentDesigns))
const attachableDesigns = computed<Design[]>(() => design.doneRecentDesigns)

const pendingDesignId = ref<string | null>(null)

async function selectDesign(designId: string): Promise<void> {
  if (pendingDesignId.value) return
  pendingDesignId.value = designId
  const ok = await content.attachDesignToPost({ postId: props.postId, designId })
  pendingDesignId.value = null
  if (ok) open.value = false
}
</script>

<template>
  <AppDialog
    v-model:open="open"
    dismissible
    width="md"
    :title="t('content.designPicker.title')"
    :description="t('content.designPicker.description')"
  >
    <div v-if="isLoadingDesigns" class="grid place-items-center py-24">
      <AppSpinner size="md" :label="t('content.designPicker.loading')" />
    </div>

    <AppEmptyState
      v-else-if="attachableDesigns.length === 0"
      size="compact"
      :title="t('content.designPicker.emptyTitle')"
      :description="t('content.designPicker.emptyDescription')"
    />

    <div v-else class="grid grid-cols-3 gap-8">
      <DesignThumbnail
        v-for="item in attachableDesigns"
        :key="item.id"
        :design="item"
        @open="selectDesign(item.id)"
      />
    </div>
  </AppDialog>
</template>
